import { Dungeon } from '../dungeon/Dungeon';
import { Player } from '../player/Player';
import { Star } from '../entities/Star';
import { SpeedBoost } from '../entities/SpeedBoost';
import { WebTrap } from '../entities/WebTrap';
import { TurnaroundTrap } from '../entities/TurnaroundTrap';
import { Breadcrumbs } from '../entities/Breadcrumbs';
import { Arrow } from '../entities/Arrow';
import { CellType } from '../types';

export class Minimap {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dungeon: Dungeon;
  private seen: boolean[][];  // Track which cells have been seen

  private readonly maxSize = 200;         // Maximum minimap size in pixels
  private readonly cellSize: number;      // Calculated based on dungeon size
  private readonly width: number;         // Actual canvas width
  private readonly height: number;        // Actual canvas height
  private readonly padding = 10;          // Padding from screen edge
  private readonly opacity = 0.7;
  private readonly viewDistance = 10;     // How far the player can see (in cells)

  // Colors
  private readonly wallColor = '#444';
  private readonly floorColor = '#222';
  private readonly playerColor = '#0f0';
  private readonly backgroundColor = 'rgba(0, 0, 0, 0.5)';

  // Animation time for speed boost color cycling
  private time = 0;

  constructor(dungeon: Dungeon) {
    this.dungeon = dungeon;

    // Calculate cell size to fit entire dungeon within maxSize
    const maxDimension = Math.max(dungeon.width, dungeon.height);
    this.cellSize = Math.floor(this.maxSize / maxDimension);
    this.width = dungeon.width * this.cellSize;
    this.height = dungeon.height * this.cellSize;

    // Initialize seen array - all cells start unseen
    this.seen = [];
    for (let z = 0; z < dungeon.height; z++) {
      this.seen[z] = [];
      for (let x = 0; x < dungeon.width; x++) {
        this.seen[z][x] = false;
      }
    }

    // Create minimap canvas
    this.canvas = document.createElement('canvas');
    this.canvas.id = 'minimap';
    this.canvas.width = this.width;
    this.canvas.height = this.height;
    this.canvas.style.position = 'absolute';
    this.canvas.style.top = `${this.padding}px`;
    this.canvas.style.right = `${this.padding}px`;
    this.canvas.style.opacity = String(this.opacity);
    this.canvas.style.borderRadius = '4px';
    this.canvas.style.pointerEvents = 'none';

    const ctx = this.canvas.getContext('2d');
    if (!ctx) {
      throw new Error('Could not get 2D context for minimap');
    }
    this.ctx = ctx;

    document.body.appendChild(this.canvas);
  }

  private hasLineOfSight(x1: number, z1: number, x2: number, z2: number): boolean {
    // Bresenham's line algorithm to check for walls between two points
    const dx = Math.abs(x2 - x1);
    const dz = Math.abs(z2 - z1);
    const sx = x1 < x2 ? 1 : -1;
    const sz = z1 < z2 ? 1 : -1;
    let err = dx - dz;

    let x = x1;
    let z = z1;

    while (true) {
      // If we hit a wall before reaching the target, no line of sight
      if (x !== x1 || z !== z1) {
        if (!this.dungeon.isWalkable(x, z)) {
          // But we can see the wall itself
          return x === x2 && z === z2;
        }
      }

      if (x === x2 && z === z2) {
        return true;
      }

      const e2 = 2 * err;
      if (e2 > -dz) {
        err -= dz;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        z += sz;
      }
    }
  }

  private updateVisibility(player: Player): void {
    const pos = player.getPosition();
    const playerCellX = Math.floor(pos.x / this.dungeon.cellSize);
    const playerCellZ = Math.floor(pos.z / this.dungeon.cellSize);

    // Check cells within view distance
    for (let dz = -this.viewDistance; dz <= this.viewDistance; dz++) {
      for (let dx = -this.viewDistance; dx <= this.viewDistance; dx++) {
        const cellX = playerCellX + dx;
        const cellZ = playerCellZ + dz;

        // Skip out of bounds
        if (cellX < 0 || cellX >= this.dungeon.width ||
            cellZ < 0 || cellZ >= this.dungeon.height) {
          continue;
        }

        // Skip if already seen
        if (this.seen[cellZ][cellX]) {
          continue;
        }

        // Check if within circular view distance
        const dist = Math.sqrt(dx * dx + dz * dz);
        if (dist > this.viewDistance) {
          continue;
        }

        // Check line of sight
        if (this.hasLineOfSight(playerCellX, playerCellZ, cellX, cellZ)) {
          this.seen[cellZ][cellX] = true;
        }
      }
    }
  }

  private isSeen(x: number, z: number): boolean {
    if (x < 0 || x >= this.dungeon.width || z < 0 || z >= this.dungeon.height) {
      return false;
    }
    return this.seen[z][x];
  }

  revealAll(): void {
    for (let z = 0; z < this.dungeon.height; z++) {
      for (let x = 0; x < this.dungeon.width; x++) {
        this.seen[z][x] = true;
      }
    }
  }

  render(
    player: Player,
    stars?: Star[],
    speedBoost?: SpeedBoost | null,
    webTrap?: WebTrap | null,
    turnaroundTrap?: TurnaroundTrap | null,
    breadcrumbs?: Breadcrumbs | null,
    arrow?: Arrow | null,
    levelPreviouslyCompleted?: boolean
  ): void {
    const ctx = this.ctx;
    const pos = player.getPosition();
    const yaw = player.camera.yaw;

    // Update animation time
    this.time += 0.016; // Approximate frame time

    // Update which cells the player can see
    this.updateVisibility(player);

    // Build a map of cell positions to star colors for uncollected stars
    const starCellColors = new Map<string, string>();
    if (stars) {
      for (let i = 0; i < stars.length; i++) {
        const star = stars[i];
        if (!star.collected) {
          const cellX = Math.floor(star.position.x / this.dungeon.cellSize);
          const cellZ = Math.floor(star.position.z / this.dungeon.cellSize);
          // Generate color using same HSL spectrum as Renderer
          const hue = (i / stars.length) * 360;
          const color = hslToRgbString(hue, 1.0, 0.5);
          starCellColors.set(`${cellX},${cellZ}`, color);
        }
      }
    }

    // Clear with background
    ctx.fillStyle = this.backgroundColor;
    ctx.fillRect(0, 0, this.width, this.height);

    // Draw all cells (only if seen)
    for (let z = 0; z < this.dungeon.height; z++) {
      for (let x = 0; x < this.dungeon.width; x++) {
        // Only draw if the player has seen this cell
        if (!this.isSeen(x, z)) continue;

        const cell = this.dungeon.getCell(x, z);
        if (!cell) continue;

        // Convert cell position to minimap pixels
        const mapX = x * this.cellSize;
        const mapZ = z * this.cellSize;

        // Check if this cell has an uncollected star
        const starColor = starCellColors.get(`${x},${z}`);
        if (starColor) {
          ctx.fillStyle = starColor;
        } else {
          ctx.fillStyle = cell.type === CellType.SOLID ? this.wallColor : this.floorColor;
        }
        ctx.fillRect(mapX, mapZ, this.cellSize - 1, this.cellSize - 1);
      }
    }

    // Draw speed boost as a circle (if visible and not collected)
    if (speedBoost && !speedBoost.collected) {
      const boostCellX = Math.floor(speedBoost.position.x / this.dungeon.cellSize);
      const boostCellZ = Math.floor(speedBoost.position.z / this.dungeon.cellSize);

      if (this.isSeen(boostCellX, boostCellZ)) {
        const boostMapX = (boostCellX + 0.5) * this.cellSize;
        const boostMapZ = (boostCellZ + 0.5) * this.cellSize;
        const radius = (this.cellSize - 2) / 2;

        // Cycle through spectrum like the spotlight (0.5 cycles per second)
        const hue = (this.time * 0.5 * 360) % 360;
        const color = hslToRgbString(hue, 1.0, 0.5);

        ctx.beginPath();
        ctx.arc(boostMapX, boostMapZ, radius, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Draw web trap marker
    if (webTrap) {
      const showSkull = webTrap.triggered || levelPreviouslyCompleted;
      if (showSkull) {
        this.drawSkull(ctx, webTrap.cellX, webTrap.cellZ);
      } else if (this.isSeen(webTrap.cellX, webTrap.cellZ)) {
        // Subtle web circle when visible and untriggered
        const wx = (webTrap.cellX + 0.5) * this.cellSize;
        const wz = (webTrap.cellZ + 0.5) * this.cellSize;
        const r = (this.cellSize - 2) / 2;
        ctx.beginPath();
        ctx.arc(wx, wz, r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0, 255, 255, 0.4)';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Draw turnaround trap marker (skull only if triggered or previously completed)
    if (turnaroundTrap) {
      const showSkull = turnaroundTrap.triggered || levelPreviouslyCompleted;
      if (showSkull) {
        this.drawSkull(ctx, turnaroundTrap.cellX, turnaroundTrap.cellZ);
      }
    }

    // Draw breadcrumbs pickup
    if (breadcrumbs && !breadcrumbs.collected) {
      const bcCellX = Math.floor(breadcrumbs.position.x / this.dungeon.cellSize);
      const bcCellZ = Math.floor(breadcrumbs.position.z / this.dungeon.cellSize);
      if (this.isSeen(bcCellX, bcCellZ)) {
        const bx = (bcCellX + 0.5) * this.cellSize;
        const bz = (bcCellZ + 0.5) * this.cellSize;
        const r = (this.cellSize - 2) / 2;
        ctx.beginPath();
        ctx.arc(bx, bz, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgb(255, 153, 26)';
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Draw footprint trail
    if (breadcrumbs && breadcrumbs.collected) {
      ctx.fillStyle = 'rgba(255, 153, 26, 0.6)';
      for (const key of breadcrumbs.footprints.keys()) {
        const [cx, cz] = key.split(',').map(Number);
        if (!this.isSeen(cx, cz)) continue;
        const fx = (cx + 0.5) * this.cellSize;
        const fz = (cz + 0.5) * this.cellSize;
        ctx.beginPath();
        ctx.arc(fx, fz, 2, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // Draw arrow pickup
    if (arrow && !arrow.collected) {
      const arCellX = Math.floor(arrow.position.x / this.dungeon.cellSize);
      const arCellZ = Math.floor(arrow.position.z / this.dungeon.cellSize);
      if (this.isSeen(arCellX, arCellZ)) {
        const ax = (arCellX + 0.5) * this.cellSize;
        const az = (arCellZ + 0.5) * this.cellSize;
        const r = (this.cellSize - 2) / 2;
        ctx.beginPath();
        ctx.arc(ax, az, r, 0, Math.PI * 2);
        ctx.fillStyle = 'rgb(26, 230, 255)';
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1;
        ctx.stroke();
      }
    }

    // Calculate player position on minimap
    const playerCellX = pos.x / this.dungeon.cellSize;
    const playerCellZ = pos.z / this.dungeon.cellSize;
    const playerMapX = playerCellX * this.cellSize;
    const playerMapZ = playerCellZ * this.cellSize;

    // Draw player marker (triangle rotated to show facing direction)
    ctx.save();
    ctx.translate(playerMapX, playerMapZ);
    ctx.rotate(-yaw);  // Rotate marker to show facing direction
    ctx.fillStyle = this.playerColor;
    ctx.beginPath();
    ctx.moveTo(0, -5);   // Top point
    ctx.lineTo(-3, 3);   // Bottom left
    ctx.lineTo(3, 3);    // Bottom right
    ctx.closePath();
    ctx.fill();
    ctx.restore();

    // Draw border
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 1;
    ctx.strokeRect(0, 0, this.width, this.height);
  }

  private drawSkull(ctx: CanvasRenderingContext2D, cellX: number, cellZ: number): void {
    if (!this.isSeen(cellX, cellZ)) return;

    const cx = (cellX + 0.5) * this.cellSize;
    const cz = (cellZ + 0.5) * this.cellSize;
    const size = (this.cellSize - 2) / 2;

    // Red circle head
    ctx.beginPath();
    ctx.arc(cx, cz, size, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(200, 0, 0, 0.8)';
    ctx.fill();

    // Black dot eyes
    const eyeOffset = size * 0.3;
    const eyeRadius = size * 0.18;
    ctx.fillStyle = 'black';
    ctx.beginPath();
    ctx.arc(cx - eyeOffset, cz - eyeOffset * 0.3, eyeRadius, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(cx + eyeOffset, cz - eyeOffset * 0.3, eyeRadius, 0, Math.PI * 2);
    ctx.fill();

    // Mouth line
    ctx.strokeStyle = 'black';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(cx - eyeOffset * 0.8, cz + size * 0.35);
    ctx.lineTo(cx + eyeOffset * 0.8, cz + size * 0.35);
    ctx.stroke();
  }

  destroy(): void {
    this.canvas.remove();
  }
}

// Convert HSL to CSS RGB string (h: 0-360, s: 0-1, l: 0-1)
function hslToRgbString(h: number, s: number, l: number): string {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;

  let r = 0, g = 0, b = 0;
  if (h < 60) { r = c; g = x; b = 0; }
  else if (h < 120) { r = x; g = c; b = 0; }
  else if (h < 180) { r = 0; g = c; b = x; }
  else if (h < 240) { r = 0; g = x; b = c; }
  else if (h < 300) { r = x; g = 0; b = c; }
  else { r = c; g = 0; b = x; }

  const ri = Math.round((r + m) * 255);
  const gi = Math.round((g + m) * 255);
  const bi = Math.round((b + m) * 255);
  return `rgb(${ri},${gi},${bi})`;
}
